import { cn } from "@/utils/theme";
import {
  Icon,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
} from "@elaniintc/goes-design-system";
import { useEffect, type ReactNode } from "react";
import { useIntersectionObserver } from "usehooks-ts";

interface SelectOption {
  value: string;
  label: string;
  icon?: ReactNode;
  avatar?: ReactNode;
  iconPosition?: string; // left | right
  indicator?: ReactNode;
}
interface SelectInputProps {
  name: string;
  placeholder: string;
  value: string;
  options: SelectOption[];
  onChange: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  hasMore?: boolean;
  showIndicator?: boolean;
  optionTextClassName?: string;
  triggerClassName?: string;
  group?:
    | "down"
    | "down-left"
    | "down-right"
    | "left"
    | "right"
    | "up"
    | "up-left"
    | "up-right"
    | "single"
    | "middle"
    | "middle-right"
    | "middle-left"
    | "middle-up";
  ref?: (node?: Element | null | undefined) => void;
}

export const SelectInput = ({
  onChange,
  value,
  name,
  options,
  isLoading = false,
  isDisabled = false,
  placeholder,
  hasMore = false,
  showIndicator = false,
  optionTextClassName,
  group,
  triggerClassName,
  ref,
}: SelectInputProps) => {
  return (
    <Select onValueChange={onChange} value={value} name={name}>
      <SelectTrigger
        className={cn(
          value ? "min-w-0" : "text-body-quiet",
          "w-full",
          triggerClassName,
        )}
        isLoading={isLoading}
        disabled={isDisabled}
        id={name}
        group={group}
      >
        {value ? (
          <div className="flex items-center gap-2 truncate">
            {(() => {
              const selectedOption = options.find(
                (option) => option.value === value,
              );
              if (!selectedOption) return null;
              return (
                <>
                  {selectedOption.iconPosition?.toLowerCase() === "left" &&
                    selectedOption.icon}
                  <p className="truncate">{selectedOption.label || ""}</p>
                  {selectedOption.iconPosition?.toLowerCase() === "right" &&
                    selectedOption.icon}
                </>
              );
            })()}
          </div>
        ) : (
          <p className="truncate text-body-quiet">{placeholder}</p>
        )}
      </SelectTrigger>
      <SelectContent className="max-h-[340px] max-w-[var(--radix-select-trigger-width)] overflow-y-auto border border-neutral py-2">
        {options.map(({ label, value, avatar, icon, iconPosition }) => {
          return (
            <SelectItem
              showIndicator={showIndicator}
              key={value}
              value={value}
              className={cn("w-full flex", optionTextClassName)}
            >
              <div className="flex-1 w-full flex items-center justify-between gap-2">
                {iconPosition?.toLowerCase() === "left" && icon}
                {avatar}
                <p className="truncate pr-2 flex-1">{label}</p>
                {iconPosition?.toLowerCase() === "right" && icon}
              </div>
            </SelectItem>
          );
        })}

        {hasMore && <div ref={ref} className="w-full py-2" />}
        {isLoading && (
          <div className="flex items-center justify-center py-2">
            <Icon
              type="far"
              name="spinner-third"
              className="animate-spin text-on-disabled"
            />
          </div>
        )}
      </SelectContent>
    </Select>
  );
};
interface Props {
  name: string;
  label: string;
  required?: boolean;
  placeholder: string;
  value: string;
  options: SelectOption[];
  error?: string;
  onChange: (value: string) => void;
  isLoading?: boolean;
  isDisabled?: boolean;
  onLoadMore?: () => void;
  hasMore?: boolean;
  showIndicator?: boolean;
  optionTextClassName?: string;
  srOnlyLabel?: boolean; // Add this line
  fullWidth?: boolean;
  triggerClassName?: string;
}

const SelectField = ({
  onChange,
  value,
  name,
  label,
  required,
  options,
  isLoading = false,
  isDisabled = false,
  placeholder,
  hasMore = false,
  error,
  onLoadMore,
  showIndicator = false,
  optionTextClassName,
  srOnlyLabel,
  fullWidth = false,
  triggerClassName,
}: Props) => {
  const [ref, isIntercepting, entry] = useIntersectionObserver({});

  useEffect(() => {
    if (isIntercepting && !isLoading && entry?.isIntersecting && hasMore) {
      // Call the onLoadMore function if it exists
      if (typeof onLoadMore === "function") {
        onLoadMore();
      }
    }
  }, [isIntercepting, isLoading, onLoadMore, entry, hasMore]);

  return (
    <div className={cn("flex flex-col gap-1", fullWidth && "w-full")}>
      <label
        className={cn(
          "font-semibold text-body-loud text-sm leading-2xl",
          isDisabled && "!text-on-disabled",
          srOnlyLabel && "sr-only",
        )}
        htmlFor={name}
      >
        {label}{" "}
        {required && (
          <span
            className={cn(
              "text-sm text-danger",
              isDisabled && "!text-on-disabled",
            )}
          >
            *
          </span>
        )}
      </label>
      <SelectInput
        name={name}
        placeholder={placeholder}
        value={value}
        options={options}
        isLoading={isLoading}
        isDisabled={isDisabled}
        showIndicator={showIndicator}
        optionTextClassName={optionTextClassName}
        triggerClassName={triggerClassName}
        hasMore={hasMore}
        onChange={onChange}
        ref={ref}
      />
      {error && (
        <p className="text-danger text-xs leading-xl font-normal">
          <Icon type="far" name="circle-xmark" className="mr-1.5" />
          {error}
        </p>
      )}
    </div>
  );
};

export default SelectField;
